import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import Grid_Select from 'src/components/common/gridWrapper/Grid_Select';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultMST1030_R01Dto, MST1030_R01Dto } from './Dto_MST1030_R01';
import useMST1030_R01Store, { TabType } from './Store_MST1030_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const MST1030_R01_TABS: { id: TabType; label: string }[] = [
	{ id: 'forward', label: 'BOM 정전개' },
	{ id: 'reverse', label: 'BOM 역전개' },
];

interface Props {
	gridRef: React.RefObject<AgGridReact<MST1030_R01Dto> | null>;
	onTabChange: (tab: TabType) => Promise<void>;
}

export const Table_MST1030_R01 = (props: Props) => {
	const { gridRef, onTabChange } = props;

	const {
		activeTab,
		tabData,
		tabColumnStates, setTabColumnState,
		tabScrollTop, setTabScrollTop,
		selectedMST1030_R01List, setSelectedMST1030_R01List,
		initSelectedMST1030_R01List,
	} = useMST1030_R01Store();

	const scrollTop = tabScrollTop[activeTab];
	const handleSetScrollTop = useCallback(
		(top: number | null) => setTabScrollTop(activeTab, top),
		[activeTab, setTabScrollTop],
	);

	const currentList = tabData[activeTab];
	const currentColumnState = tabColumnStates[activeTab];
	const COLUMN_STATE_KEY = `MST1030_R01-${activeTab}-grid-column-state`;

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: currentList,
		defaultGridData: defaultMST1030_R01Dto,
		selectedRow: selectedMST1030_R01List,
	});

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		level: '단계',
		itemNm: '품목명',
		itemSpec: '규격',
		bomSeq: '작업순서',
		bomUseyn: '사용여부',
		prntItemQty: '기준수량',
		prntItemUnit: '기준단위',
		childItemUsage: '소요량',
		childItemUnit: '소요단위',
		lossPercent: 'Loss율(%)',
		remark: '비고',
		bomUpdateNo: 'BOM등록코드',
	};

	const getItemCodeCellStyle = useCallback((params: { data?: MST1030_R01Dto; node: { rowPinned?: string | null } }) => {
		const currentLevel = params.data?.level || 0;
		const paddingLeft = currentLevel === 0 ? 11 : currentLevel * 20;
		return {
			paddingLeft: `${paddingLeft}px`,
			cursor: params.node.rowPinned !== 'bottom' ? 'pointer' : 'default',
		};
	}, []);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemNm')(params), []);

	const getCellStyle_itemCd = useCallback((params: unknown, key: string) => ({
		...getCellStyle(params, key),
		...getItemCodeCellStyle(params as { data?: MST1030_R01Dto; node: { rowPinned?: string | null } }),
	}), [getCellStyle, getItemCodeCellStyle]);

	const baseColumnDefs: ColDef<MST1030_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle_itemCd(p, 'itemCd'),
			editable: false,
			cellRenderer: (p: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) => {
				if (p.node.rowPinned === 'bottom') return '';
				const currentLevel = p.data?.level || 0;
				const itemCd = p.data?.itemCd || '';
				return currentLevel > 0 ? `└ ${itemCd}` : itemCd;
			},
		},
		{
			headerName: '단계', colId: 'level', field: 'level', width: 80,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'level'),
			editable: false,
			cellRenderer: (p: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) => {
				if (p.node.rowPinned === 'bottom') return '';
				const currentLevel = p.data?.level || 0;
				return currentLevel === 0 ? ' 0' : `${currentLevel}`;
			},
		},
		{
			headerName: '품목명', colId: 'itemNm', field: 'itemNm', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			editable: false,
		},
		{
			headerName: '작업순서', colId: 'bomSeq', field: 'bomSeq', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bomSeq'),
			editable: false,
		},
		{
			headerName: '사용여부', colId: 'bomUseyn', field: 'bomUseyn', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bomUseyn'),
			editable: false,
		},
		{
			headerName: '기준수량', colId: 'prntItemQty', field: 'prntItemQty', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'prntItemQty'),
			editable: false,
		},
		{
			headerName: '기준단위', colId: 'prntItemUnit', field: 'prntItemUnit', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'prntItemUnit'),
			editable: false,
		},
		{
			headerName: '소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'childItemUsage'),
			editable: false,
		},
		{
			headerName: '소요단위', colId: 'childItemUnit', field: 'childItemUnit', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'childItemUnit'),
			editable: false,
		},
		{
			headerName: 'Loss율(%)', colId: 'lossPercent', field: 'lossPercent', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'lossPercent'),
			editable: false,
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			editable: false,
		},
		{
			headerName: 'BOM등록코드', colId: 'bomUpdateNo', field: 'bomUpdateNo', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'bomUpdateNo'),
			editable: false,
		},
	], [getCellStyle, getCellStyle_itemCd]);

	const effectiveColumnState = useMemo(() =>
		Array.isArray(currentColumnState) && currentColumnState.length === 0
			? null : currentColumnState
	, [currentColumnState]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST1030_R01Dto>[] = useMemo(() => {
		if (effectiveColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST1030_R01Dto>({
			baseColumnDefs,
			columnState: effectiveColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [effectiveColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedMST1030_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) =>
			setSelectedMST1030_R01List(ids.map(String)),
	}), [selectedMST1030_R01List, setSelectedMST1030_R01List]);

	const rowClassRules = useMemo(() => ({
		...createRowClassRules<MST1030_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedMST1030_R01List,
			deletedList: [],
		}),
		'bom-level-0': (params: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) =>
			!params.node.rowPinned && (params.data?.level || 0) === 0,
		'bom-level-1': (params: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) =>
			!params.node.rowPinned && (params.data?.level || 0) === 1,
		'bom-level-2': (params: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) =>
			!params.node.rowPinned && (params.data?.level || 0) === 2,
		'bom-level-3': (params: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) =>
			!params.node.rowPinned && (params.data?.level || 0) === 3,
		'bom-level-4-plus': (params: { node: { rowPinned?: string | null }; data?: MST1030_R01Dto }) =>
			!params.node.rowPinned && (params.data?.level || 0) >= 4,
	}), [createRowClassRules, selectedMST1030_R01List]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		itemNm: `${currentList.length.toLocaleString()}건`,
		editable: false,
	}]), [currentList.length]);

	return (
		<div className="w-full h-full flex flex-col">
			<div
				className="flex items-end mb-[0.1px] bg-white h-[25px]"
				role="tablist"
			>
				{MST1030_R01_TABS.map((tab) => {
					const isActive = activeTab === tab.id;
					return (
						<button
							key={tab.id}
							onClick={() => onTabChange(tab.id)}
							role="tab"
							aria-selected={isActive}
							className={`h-[26px] px-3 text-[12px] font-medium transition-colors border border-gray-300 border-b-0
								${isActive
									? 'bg-white text-black-700 -mb-px shadow-sm'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
								}`}
						>
							{tab.label}
						</button>
					);
				})}
			</div>

			<div className="flex-1">
				<GridContextWrapper<MST1030_R01Dto>
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					enableCellCalculation
					initSelected={initSelectedMST1030_R01List}
					defaultRowData={defaultMST1030_R01Dto}
					baseColumnDefs={effectiveColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={(state) => setTabColumnState(activeTab, state ?? [])}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Select<MST1030_R01Dto>
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						getRowId={getRowId}
						rowClassRules={rowClassRules}
						shiftRangeSelection={shiftRangeSelectionConfig}
						onCellKeyDown={undefined}
						pinnedBottomRowData={pinnedBottomRow}
						setHoveredRowIndex={setHoveredRowIndex}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={(state) => setTabColumnState(activeTab, state ?? [])}
						scrollTop={scrollTop}
						setScrollTop={handleSetScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
